/**
 * Core utilities, bridge state, selection/editable detection, spacing + logo helpers.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function coreStateLayer() {
  return String.raw`  const PASTE_SANITIZER = window.__pbPasteSanitizer || null;
  const PASTE_PROFILES = PASTE_SANITIZER && PASTE_SANITIZER.PROFILES
    ? PASTE_SANITIZER.PROFILES
    : {
      KEEP_FORMATTING: 'keep-formatting',
      MATCH_SITE_STYLE: 'match-site-style',
      PLAIN_TEXT: 'plain-text'
    };
  const DEFAULT_PASTE_PROFILE = PASTE_SANITIZER && PASTE_SANITIZER.DEFAULT_PROFILE
    ? PASTE_SANITIZER.DEFAULT_PROFILE
    : PASTE_PROFILES.MATCH_SITE_STYLE;

  function send(type, data) {
    window.parent.postMessage({ source: 'pb-iframe', nonce: __PB_NONCE__, type: type, data: data }, '*');
  }

  var _styleInvalidationFrame = 0;
  var _styleInvalidationReason = '';
  function markStyleSnapshotDirty(reason) {
    _styleInvalidationReason = reason || _styleInvalidationReason || 'style-change';
    if (_styleInvalidationFrame) return;
    _styleInvalidationFrame = requestAnimationFrame(function() {
      _styleInvalidationFrame = 0;
      send('style-invalidated', { reason: _styleInvalidationReason || 'style-change' });
      _styleInvalidationReason = '';
    });
  }

  function _pbIgnoreElementForNode(node) {
    if (!node) return null;
    var el = node.nodeType === 1 ? node : (node.parentElement || node.parentNode);
    if (el && el.nodeType === 1 && el.matches && el.matches('[data-pb-ignore]')) return el;
    return el && el.closest ? el.closest('[data-pb-ignore]') : null;
  }

  function _pbIsIgnoredMutation(mutation) {
    if (!mutation) return false;
    if (mutation.type !== 'childList') return !!_pbIgnoreElementForNode(mutation.target);
    var added = Array.prototype.slice.call(mutation.addedNodes || []);
    var removed = Array.prototype.slice.call(mutation.removedNodes || []);
    var touched = added.concat(removed);
    if (!touched.length) return !!_pbIgnoreElementForNode(mutation.target);
    for (var i = 0; i < touched.length; i++) {
      if (!_pbIgnoreElementForNode(touched[i])) return false;
    }
    return true;
  }

  function _pbOnlyIgnoredMutations(mutations) {
    if (!mutations || !mutations.length) return false;
    for (var i = 0; i < mutations.length; i++) {
      if (!_pbIsIgnoredMutation(mutations[i])) return false;
    }
    return true;
  }

  function initScrollAnimations() {
    try {
      if (typeof window.__PB_INIT_SCROLL_ANIMATIONS__ === 'function') {
        window.__PB_INIT_SCROLL_ANIMATIONS__();
      }
    } catch (err) { console.warn('[pb-bridge] scroll animation init failed:', err); }
  }

  /**
   * EA-3 — the entrance mode operations, wired through ONE accessor.
   *
   * Every op is self-catching inside the runtime, and this wrapper tolerates the
   * runtime being absent entirely: a canvas document for a project with no
   * motion of any kind ships no runtime, and a mode change there must be a
   * no-op rather than a thrown message handler.
   */
  function entranceOps() {
    return window.__PB_ENTRANCE__ || null;
  }

  function setEntranceSuppressed(suppressed) {
    try {
      var ops = entranceOps();
      if (ops) ops.setEntranceSuppressed(suppressed);
    } catch (err) { console.warn('[pb-bridge] entrance suppression failed:', err); }
  }

  function replayBuilderEntrances() {
    try {
      var ops = entranceOps();
      if (ops) ops.replayBuilderEntrances();
    } catch (err) { console.warn('[pb-bridge] entrance replay failed:', err); }
  }

  function setTemplateEntrancesEnabled(enabled) {
    try {
      var ops = entranceOps();
      if (ops) ops.setTemplateEntrancesEnabled(enabled);
    } catch (err) { console.warn('[pb-bridge] template entrance toggle failed:', err); }
  }

  // EA-10 — the one-shot scoped preview. Same tolerance: no runtime, no-op.
  function previewEntranceOnce(scopeSelector) {
    try {
      var ops = entranceOps();
      if (ops && ops.previewEntranceOnce) ops.previewEntranceOnce(scopeSelector);
    } catch (err) { console.warn('[pb-bridge] entrance preview failed:', err); }
  }

  function getCurrentZoom() {
    var z = parseFloat(document.documentElement.style.zoom || '');
    return (isFinite(z) && z > 0) ? z : 1;
  }

  function getScrollRoot() {
    return window.__PB_SCROLL_ROOT__ || document.scrollingElement || document.documentElement;
  }

  function getDocumentContentHeight() {
    var scrollRoot = getScrollRoot();
    var body = document.body;
    return Math.max(
      scrollRoot && scrollRoot.scrollHeight ? scrollRoot.scrollHeight : 0,
      document.documentElement && document.documentElement.scrollHeight ? document.documentElement.scrollHeight : 0,
      body && body.scrollHeight ? body.scrollHeight : 0
    );
  }

  function observeResizeTarget(observer, target) {
    if (!observer || !target) return;
    try {
      observer.observe(target);
    } catch (err) { console.warn('[pb-bridge] resize observe failed:', err); }
  }

  function _isHeadingLikeEditable(el) {
    if (!el) return false;
    var tag = (el.tagName || '').toUpperCase();
    return /^H[1-6]$/.test(tag) || tag === 'BUTTON';
  }

  function _getPasteTokenColors() {
    var styles = window.getComputedStyle(document.documentElement);
    return {
      accent: (styles.getPropertyValue('--primary') || '').trim(),
      heading: (styles.getPropertyValue('--headerColor') || '').trim(),
      body: (styles.getPropertyValue('--bodyTextColor') || '').trim()
    };
  }

  function sanitizeHTML(html, profile, editableEl) {
    if (!PASTE_SANITIZER || typeof PASTE_SANITIZER.sanitizeClipboardPayload !== 'function') {
      return String(html || '');
    }
    var result = PASTE_SANITIZER.sanitizeClipboardPayload({
      html: String(html || ''),
      profile: profile || PASTE_PROFILES.KEEP_FORMATTING,
      targetTagName: editableEl ? editableEl.tagName : '',
      targetIsHeading: _isHeadingLikeEditable(editableEl),
      tokenColors: _getPasteTokenColors()
    });
    if (result && result.ok) return result.html || '';
    return '';
  }

  // --- State ---
  var activeEditable = null;
  var activeEditableInitialHTML = null;
  var activeEditableInitialTagName = null;
  var debounceTimer = null;
  var lastRange = null;
  var _editSessionCounter = 1;
  var _activeEditSessionId = 0;
  var _selectedEditable = null;  // element tracked for continuous rect updates
  var _selectedEditableMutationObserver = null;
  var _lastPastePayload = null;
  var _lastPasteRangeSnapshot = null;
  var _lastPasteInsertedNodes = null;
  var _lastPasteReplacedNodes = null;
  var _lastUndonePastePayload = null;
  var _editOperationUndoStack = [];
  var _editOperationRedoStack = [];
  var _isComposingText = false;
  var _plainTextOnly = false;
  var _plainTextPasteRequestedUntil = 0;
  var _escapeCancelArmedUntil = 0;
  var _intentionallyRemovedProtectedPaths = [];
  var _activeEditableDirty = false;
  var _suspendedDragTargets = [];
  var _selectedSectionInstanceId = null;
  var _hoveredSectionInstanceId = null;
  // 16A1 — co-selected section ids (multi-select). The scalar above stays the
  // LEAD; these are the other members, stamped with data-pb-section-multi.
  var _multiSelectedInstanceIds = [];

  function _setReorderActive(active) {
    try {
      if (active) {
        document.documentElement.setAttribute('data-pb-reorder-active', '');
      } else {
        document.documentElement.removeAttribute('data-pb-reorder-active');
      }
    } catch (err) { console.warn('[pb-bridge] reorder-active toggle failed:', err); }
    // Setting an attribute EXECUTES NOTHING. The drivers poll
    // "data-pb-reorder-active" on their own frames, but a builder entrance is
    // armed once by an observer and would stay armed through a drag, so
    // suppression is an explicit call. In practice the editing canvas is
    // already suppressed by its document marker, which makes this a cheap belt
    // — and a belt is what it should be: the day reorder becomes reachable from
    // a surface that is NOT suppressed, this is the line that keeps it correct.
    if (active) setEntranceSuppressed(true);
  }

  function _rememberDragTarget(el, seen) {
    if (!el || !el.setAttribute || seen.indexOf(el) !== -1) return;
    seen.push(el);
    _suspendedDragTargets.push({
      el: el,
      hadDraggable: el.hasAttribute('draggable'),
      draggable: el.getAttribute('draggable')
    });
    el.setAttribute('draggable', 'false');
  }

  function _suspendDragForTextEditing(editable) {
    _restoreSuspendedDragTargets();
    if (!editable || !isTextEditable(editable)) return;

    var seen = [];
    _rememberDragTarget(editable, seen);

    if (editable.closest) {
      _rememberDragTarget(editable.closest('a[href]'), seen);
      _rememberDragTarget(editable.closest('[draggable="true"]'), seen);
    }

    var nested = editable.querySelectorAll
      ? editable.querySelectorAll('a[href], img, [draggable="true"]')
      : [];
    for (var i = 0; i < nested.length; i++) {
      _rememberDragTarget(nested[i], seen);
    }
  }

  function _restoreSuspendedDragTargets() {
    for (var i = 0; i < _suspendedDragTargets.length; i++) {
      var record = _suspendedDragTargets[i];
      if (!record || !record.el || !record.el.setAttribute) continue;
      if (record.hadDraggable) {
        record.el.setAttribute('draggable', record.draggable || '');
      } else {
        record.el.removeAttribute('draggable');
      }
    }
    _suspendedDragTargets = [];
  }

  function disconnectSelectedEditableObserver() {
    if (_selectedEditableMutationObserver) {
      _selectedEditableMutationObserver.disconnect();
      _selectedEditableMutationObserver = null;
    }
  }

  function observeSelectedEditable(el) {
    disconnectSelectedEditableObserver();
    if (!el) return;
    _selectedEditableMutationObserver = new MutationObserver(function(mutations) {
      if (_pbOnlyIgnoredMutations(mutations)) return;
      if (typeof markElementRectDirty === 'function') {
        markElementRectDirty();
      }
    });
    _selectedEditableMutationObserver.observe(el, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function clearSelectedEditable(reason) {
    var hadSelectedEditable = !!_selectedEditable;
    _clearSelectedState(_selectedEditable);
    _selectedEditable = null;
    disconnectSelectedEditableObserver();
    if (hadSelectedEditable) {
      send('selected-editable-cleared', { reason: reason || 'cleared' });
    }
  }

  function findInstance(el) {
    var wrapper = el.closest('[data-pb-instance]');
    return wrapper ? wrapper.getAttribute('data-pb-instance') : null;
  }

  function _kindOf(el) {
    return (el && el.getAttribute && el.getAttribute(KIND_ATTR)) || 'unknown';
  }

  function _kindLabelOf(el) {
    var kind = _kindOf(el);
    return KIND_LABELS[kind] || KIND_LABELS.unknown || 'Element';
  }

  function getEditablePath(el) {
    var instanceId = findInstance(el);
    if (!instanceId) return null;
    var wrapper = el.closest('[data-pb-instance]');
    var editables = Array.from(wrapper.querySelectorAll('[data-pb-editable]'));
    var index = editables.indexOf(el);
    return {
      instanceId: instanceId,
      editableIndex: index,
      eid: el.getAttribute('data-pb-eid'),
      targetRef: { domain: 'dom', eid: el.getAttribute('data-pb-eid') },
      editableType: el.getAttribute('data-pb-editable'),
      clientLocked: el.hasAttribute('data-pb-client-locked'),
      kind: _kindOf(el),
      tagName: el.tagName
    };
  }

  function buildElementAncestorChain(node, boundary) {
    var chain = [];
    var hops = 0;
    var current = node;
    while (current && current !== boundary && hops < 12) {
      chain.push({
        tagName: (current.tagName || '').toLowerCase(),
        classNames: current.classList ? Array.prototype.slice.call(current.classList) : [],
        eid: current.getAttribute ? (current.getAttribute('data-pb-eid') || null) : null,
        repeatItem: !!(current.hasAttribute && current.hasAttribute('data-pb-repeat-item'))
      });
      current = current.parentElement;
      hops++;
    }
    return chain;
  }

  function getSpacingStyleBlock(style) {
    if (!style) {
      return {
        paddingTop: '0px',
        paddingRight: '0px',
        paddingBottom: '0px',
        paddingLeft: '0px',
        marginTop: '0px',
        marginRight: '0px',
        marginBottom: '0px',
        marginLeft: '0px'
      };
    }
    return {
      paddingTop: style.paddingTop,
      paddingRight: style.paddingRight,
      paddingBottom: style.paddingBottom,
      paddingLeft: style.paddingLeft,
      marginTop: style.marginTop,
      marginRight: style.marginRight,
      marginBottom: style.marginBottom,
      marginLeft: style.marginLeft
    };
  }

  function getAuthoredSpacingStyleBlock(target) {
    var out = {};
    if (!target || !target.matches) return out;
    var propMap = {
      paddingTop: 'padding-top',
      paddingRight: 'padding-right',
      paddingBottom: 'padding-bottom',
      paddingLeft: 'padding-left',
      marginTop: 'margin-top',
      marginRight: 'margin-right',
      marginBottom: 'margin-bottom',
      marginLeft: 'margin-left'
    };

    function copyDeclarations(style) {
      if (!style || typeof style.getPropertyValue !== 'function') return;
      for (var key in propMap) {
        var raw = style.getPropertyValue(propMap[key]);
        if (raw) out[key] = String(raw).trim();
      }
    }

    function walkRules(rules) {
      if (!rules) return;
      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];
        if (!rule) continue;
        if (rule.type === 1 && rule.selectorText && rule.style) {
          try {
            if (target.matches(rule.selectorText)) copyDeclarations(rule.style);
          } catch (e) {
            // Ignore selectors that cannot be used with matches(), such as pseudo-elements.
          }
        } else if (rule.cssRules) {
          if (rule.media && typeof window.matchMedia === 'function') {
            try {
              if (!window.matchMedia(rule.media.mediaText || String(rule.media)).matches) continue;
            } catch (e) { /* unparseable media query — include rules conservatively; expected */ }
          }
          try { walkRules(rule.cssRules); } catch (e) { /* cross-origin or detached sheet — expected */ }
        }
      }
    }

    for (var s = 0; s < document.styleSheets.length; s++) {
      var sheet = document.styleSheets[s];
      try { walkRules(sheet.cssRules); } catch (e) { /* cross-origin or detached sheet — expected */ }
    }
    copyDeclarations(target.style);
    return out;
  }

  function getAuthoredStyleValue(target, propertyName) {
    if (!target || !target.matches || !propertyName) return '';
    var value = '';
    function readDeclaration(style) {
      if (!style || typeof style.getPropertyValue !== 'function') return;
      var raw = style.getPropertyValue(propertyName);
      if (raw) value = String(raw).trim();
    }
    function walkRules(rules) {
      if (!rules) return;
      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];
        if (!rule) continue;
        if (rule.type === 1 && rule.selectorText && rule.style) {
          try {
            if (target.matches(rule.selectorText)) readDeclaration(rule.style);
          } catch (e) { /* selector cannot be matched directly */ }
        } else if (rule.cssRules) {
          if (rule.media && typeof window.matchMedia === 'function') {
            try {
              if (!window.matchMedia(rule.media.mediaText || String(rule.media)).matches) continue;
            } catch (e) { /* include an unparseable media rule conservatively */ }
          }
          try { walkRules(rule.cssRules); } catch (e) { /* inaccessible stylesheet */ }
        }
      }
    }
    for (var s = 0; s < document.styleSheets.length; s++) {
      try { walkRules(document.styleSheets[s].cssRules); } catch (e) { /* inaccessible stylesheet */ }
    }
    readDeclaration(target.style);
    return value;
  }

  function getPictureForSpacing(el) {
    if (!el || (el.tagName || '').toUpperCase() !== 'IMG') return null;
    return el.parentElement && el.parentElement.tagName === 'PICTURE'
      ? el.parentElement
      : (el.closest ? el.closest('picture') : null);
  }

  function getSpacingComputedForEditable(el) {
    var picture = getPictureForSpacing(el);
    return getSpacingStyleBlock(getComputedStyle(picture || el));
  }

  function getSpacingTemplateForEditable(el) {
    var picture = getPictureForSpacing(el);
    return getAuthoredSpacingStyleBlock(picture || el);
  }

  function getSpacingRectForEditable(el) {
    var target = getPictureForSpacing(el) || el;
    var rect = target.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };
  }

  function getStableClassNames(el) {
    if (!el || !el.classList) return [];
    return Array.from(el.classList).filter(function(cls) {
      return cls
        && !/^(?:active|current|selected|open|closed|first|last|odd|even|hidden|visible|is-|has-|js-|pb-)/i.test(cls)
        && !/^(?:cs-)?item\d+$/i.test(cls)
        && !/^(?:col|span|order)-?\d+$/i.test(cls);
    });
  }

  function sharesStableClass(aClasses, bEl) {
    if (!aClasses || aClasses.length === 0 || !bEl || !bEl.classList) return false;
    for (var i = 0; i < aClasses.length; i++) {
      if (bEl.classList.contains(aClasses[i])) return true;
    }
    return false;
  }

  function getAncestryForEditable(el) {
    if (!el) return [];
    var trail = [];
    var node = el;
    var hops = 0;
    while (node && node !== document.documentElement && hops < 8) {
      var entry = null;
      var tag = (node.tagName || '').toLowerCase();
      if (node.hasAttribute && node.hasAttribute('data-pb-section')) {
        entry = {
          label: 'Section',
          tagName: tag,
          eid: null,
          isSection: true,
          instanceId: node.getAttribute('data-pb-instance') || null
        };
      } else if (node.hasAttribute && node.hasAttribute('data-pb-editable')) {
        var stableClasses = getStableClassNames(node);
        var firstClass = stableClasses.length ? stableClasses[0] : '';
        entry = {
          label: firstClass ? tag + '.' + firstClass : tag,
          tagName: tag,
          eid: node.getAttribute('data-pb-eid') || null
        };
      } else if (node.classList && (
        (node.className || '').indexOf('cs-') !== -1 ||
        /(?:^|\s)[^\s]*(?:grid|card|product|article|collection|list|row)[^\s]*(?:\s|$)/i.test(node.className || '')
      )) {
        var wrapperClasses = getStableClassNames(node);
        if (wrapperClasses.length) {
          entry = { label: tag + '.' + wrapperClasses[0], tagName: tag, eid: null };
        }
      }
      if (entry) {
        // Raw parentElement steps from the selected editable to this node.
        // Unlike ancestorDepth (an index into this FILTERED trail), domHops is
        // safe to replay as a literal parentElement walk — skipped wrapper
        // nodes between trail entries can't shift the target (Tier 4 Phase 2).
        entry.domHops = hops;
        trail.push(entry);
      }
      node = node.parentElement;
      hops++;
    }
    trail = trail.reverse();
    for (var t = 0; t < trail.length; t++) {
      trail[t].ancestorDepth = trail.length - 1 - t;
    }
    return trail;
  }

  function getSpacingGroupForEid(eid, scope) {
    var selected = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(eid) + '"]');
    if (!selected) return [];
    if (scope !== 'siblings') return [selected];

    var parent = selected.parentElement;
    var selectedClasses = getStableClassNames(selected);
    if (!parent || selectedClasses.length === 0) return [selected];

    var selectedTag = selected.tagName;
    var children = Array.from(parent.children || []);
    var matches = children.filter(function(child) {
      return child.tagName === selectedTag
        && child.hasAttribute('data-pb-eid')
        && sharesStableClass(selectedClasses, child);
    });
    return matches.length ? matches : [selected];
  }

  function dispatchSyntheticClick(target) {
    if (!target) return false;
    var clickEvt;
    try {
      clickEvt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
    } catch (e) {
      clickEvt = document.createEvent('MouseEvents');
      clickEvt.initEvent('click', true, true);
    }
    target.dispatchEvent(clickEvt);
    return true;
  }

  function selectSpacingRelativeTarget(target) {
    if (!target) return;
    if (target.hasAttribute && target.hasAttribute('data-pb-editable') && target.hasAttribute('data-pb-eid')) {
      dispatchSyntheticClick(target);
      return;
    }
    var editable = target.querySelector ? target.querySelector('[data-pb-editable][data-pb-eid]') : null;
    if (editable) {
      dispatchSyntheticClick(editable);
      return;
    }
    var section = target.closest ? target.closest('[data-pb-section]') : null;
    if (section) {
      var sectionWrapper = section.closest ? section.closest('[data-pb-instance]') : null;
      var sectionInstanceId = sectionWrapper ? sectionWrapper.getAttribute('data-pb-instance') : null;
      _setSelectedSection(sectionInstanceId, { flash: true });
      send('section-clicked', { instanceId: sectionInstanceId });
    }
  }

  function resolveEditableClickTarget(rawTarget, evt) {
    if (!rawTarget || !rawTarget.closest) return null;
    var editable = rawTarget.closest('[data-pb-editable]');
    // setPointerCapture inside section JS (e.g. special-9001/9002 reels) can
    // retarget the click to the capturing ancestor, so closest() returns null
    // even though the visible click was on an editable. Fall back to
    // elementFromPoint at the click coords to recover the real target.
    if (!editable && evt && typeof evt.clientX === 'number' && typeof evt.clientY === 'number') {
      var hit = document.elementFromPoint(evt.clientX, evt.clientY);
      if (hit && hit.closest) editable = hit.closest('[data-pb-editable]');
    }
    if (!editable) return null;
    var logoWrapper = editable.closest('[data-pb-editable="logo"]');
    if (logoWrapper && logoWrapper !== editable) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Page Builder] Inner logo element retained data-pb-editable; selecting logo wrapper instead.');
      }
      return logoWrapper;
    }
    return editable;
  }

  function isLikelyDecorativeGraphic(el) {
    var kind = _kindOf(el);
    return kind === 'decoration' || kind === 'background';
  }

  function isVisibleElement(el) {
    if (!el) return false;
    var style = getComputedStyle(el);
    if (!style || style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) {
      return false;
    }
    var rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  function shouldKeepDirectEditablePriority(editable) {
    if (!editable) return false;
    // A decorative candidate may span the whole section. When the browser's
    // actual hit target is a different actionable editable (especially an
    // image), that direct target wins. Decorative elements still route to the
    // section because they identify themselves here.
    if (!isLikelyDecorativeGraphic(editable)) return true;
    var type = editable.getAttribute('data-pb-editable') || '';
    return type === 'text' || type === 'button' || type === 'logo';
  }

  function resolveDecorativeGraphicAtPoint(evt, directEditable) {
    if (!evt || shouldKeepDirectEditablePriority(directEditable)) return null;
    var rawTarget = evt.target;
    if (!rawTarget || !rawTarget.closest) return null;
    var wrapper = rawTarget.closest('[data-pb-instance]');
    if (!wrapper && directEditable && directEditable.closest) {
      wrapper = directEditable.closest('[data-pb-instance]');
    }
    if (!wrapper) return null;

    var x = evt.clientX;
    var y = evt.clientY;
    var candidates = Array.from(wrapper.querySelectorAll('[data-pb-editable]'));
    var best = null;
    var bestZ = -Infinity;
    var bestIndex = -1;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!isLikelyDecorativeGraphic(el) || !isVisibleElement(el)) continue;
      var rect = el.getBoundingClientRect();
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) continue;
      var style = getComputedStyle(el);
      var z = parseInt(style.zIndex || '0', 10);
      if (!isFinite(z)) z = 0;
      if (!best || z > bestZ || (z === bestZ && i > bestIndex)) {
        best = el;
        bestZ = z;
        bestIndex = i;
      }
    }
    return best;
  }

  // Stamped vocabulary is the selection-time source of truth.
  var DECORATIVE_INDEX_SELECTOR = '[data-pb-kind="decoration"]';
  function _decorativeCandidates(sectionEl) {
    if (!sectionEl || !sectionEl.querySelectorAll) return [];
    var all = Array.from(sectionEl.querySelectorAll(DECORATIVE_INDEX_SELECTOR));
    return all.filter(function(el) {
      var tag = (el.tagName || '').toLowerCase();
      return tag === 'svg' || tag === 'img';
    });
  }

  function _deriveDecorativeTargetKind(el) {
    return _kindOf(el) === 'background' ? 'decorative-background' : 'decorative-divider';
  }

  function _buildDecorativeDescriptor(el, evt, sectionEl) {
    if (!el || !sectionEl) return null;
    var editables = Array.from(sectionEl.querySelectorAll('[data-pb-editable]'));
    var editableIndex = editables.indexOf(el);
    var targetKind = _deriveDecorativeTargetKind(el);
    var decorativeIndex = null;
    if (targetKind === 'decorative-divider') {
      decorativeIndex = _decorativeCandidates(sectionEl).indexOf(el);
      if (decorativeIndex < 0) decorativeIndex = null;
    }
    var rect = el.getBoundingClientRect();
    return {
      owner: 'section',
      targetKind: targetKind,
      capabilities: ['replace', 'remove', 'open-library'],
      instanceId: sectionEl.getAttribute('data-pb-instance') || '',
      sectionId: sectionEl.getAttribute('data-pb-section') || '',
      eid: (el.getAttribute && el.getAttribute('data-pb-eid')) || '',
      targetRef: { domain: 'dom', eid: (el.getAttribute && el.getAttribute('data-pb-eid')) || '' },
      editableIndex: editableIndex >= 0 ? editableIndex : null,
      decorativeIndex: decorativeIndex,
      editableType: (el.getAttribute && el.getAttribute('data-pb-editable')) || '',
      kind: _kindOf(el),
      decorativeName: (el.getAttribute && el.getAttribute('data-pb-decorative-name')) || '',
      tagName: (el.tagName || '').toLowerCase(),
      rect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      },
      point: {
        top: evt.clientY,
        left: evt.clientX
      },
      zoom: getCurrentZoom()
    };
  }

  function _buildSectionMenuDescriptor(sectionEl, evt) {
    if (!sectionEl) return null;
    var rect = sectionEl.getBoundingClientRect();
    return {
      owner: 'section',
      targetKind: 'section',
      instanceId: sectionEl.getAttribute('data-pb-instance') || '',
      sectionId: sectionEl.getAttribute('data-pb-section') || '',
      rect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      },
      point: {
        top: evt.clientY,
        left: evt.clientX
      },
      zoom: getCurrentZoom()
    };
  }

  // Single source of truth for context-menu intent. Both the mousedown
  // preventDefault gate and the new contextmenu dispatcher consume this so
  // they cannot drift apart.
  // Returns: { kind: 'text-selection' | 'decorative' | 'element' | 'section' | null,
  //            deco?: Element, sectionEl?: Element, contextTarget?: Object }
  function _resolveContextMenuIntent(evt) {
    if (_shouldLetTextSelectionOwnContextMenu()) return { kind: 'text-selection' };
    var directEditable = resolveEditableClickTarget(evt.target, evt);
    var deco = resolveDecorativeGraphicAtPoint(evt, directEditable);
    if (deco) {
      var sectionForDeco = deco.closest ? deco.closest('[data-pb-instance]') : null;
      if (sectionForDeco) return { kind: 'decorative', deco: deco, sectionEl: sectionForDeco };
    }
    var contextTarget = _resolveElementContextMenuTarget(evt.target);
    if (contextTarget) return { kind: 'element', contextTarget: contextTarget };
    var sectionEl = (evt.target && evt.target.closest) ? evt.target.closest('[data-pb-instance]') : null;
    if (sectionEl) return { kind: 'section', sectionEl: sectionEl };
    return { kind: null };
  }

  function getLogoImageTargets(wrapper, variant) {
    if (!wrapper || !wrapper.querySelectorAll) return [];
    var images = Array.from(wrapper.querySelectorAll('.logo-slot img'));
    var targetVariant = variant || 'all';
    if (targetVariant === 'all') return images;
    var matches = images.filter(function(img) {
      return img.classList && img.classList.contains(targetVariant);
    });
    if (matches.length) return matches;
    if (targetVariant === 'light' && images[0]) return [images[0]];
    if (targetVariant === 'dark' && images[1]) return [images[1]];
    return images.length === 1 ? [images[0]] : [];
  }

  function ensureLogoImageTargets(wrapper, variant) {
    var existing = getLogoImageTargets(wrapper, variant);
    if (existing.length) return existing;
    if (!wrapper || !wrapper.querySelector) return [];
    var slot = wrapper.querySelector('.logo-slot');
    if (!slot) return [];
    var oldGraphic = slot.querySelector('svg, picture, img');
    var img = document.createElement('img');
    img.setAttribute('decoding', 'async');
    if (oldGraphic && oldGraphic.getAttribute) {
      var oldClass = String(oldGraphic.getAttribute('class') || '').trim();
      if (oldClass) img.setAttribute('class', oldClass);
      var ariaHidden = oldGraphic.getAttribute('aria-hidden');
      if (ariaHidden) img.setAttribute('aria-hidden', ariaHidden);
      var role = oldGraphic.getAttribute('role');
      if (role) img.setAttribute('role', role);
    }
    if (variant && variant !== 'all') img.classList.add(variant);
    slot.textContent = '';
    slot.appendChild(img);
    wrapper.removeAttribute('data-pb-logo-source-locked');
    return [img];
  }

  function applyLogoImageAttrs(img, data) {
    if (!img || !data) return;
    if (typeof data.src === 'string' && data.src) img.setAttribute('src', data.src);
    if (typeof data.alt === 'string') img.setAttribute('alt', data.alt);
    if (data.assetId) img.setAttribute('data-pb-asset', data.assetId);
    else if ('assetId' in data) img.removeAttribute('data-pb-asset');
    if (data.mimeType) img.setAttribute('data-pb-mime', data.mimeType);
    else if ('mimeType' in data) img.removeAttribute('data-pb-mime');
    if (data.width != null && isFinite(Number(data.width)) && Number(data.width) > 0) {
      img.setAttribute('width', String(Math.round(Number(data.width))));
    }
    if (data.height != null && isFinite(Number(data.height)) && Number(data.height) > 0) {
      img.setAttribute('height', String(Math.round(Number(data.height))));
    }

    var picture = img.closest('picture');
    if (picture) {
      var sources = picture.querySelectorAll('source');
      for (var i = 0; i < sources.length; i++) {
        if (typeof data.src === 'string' && data.src) sources[i].setAttribute('srcset', data.src);
        if (data.mimeType) sources[i].setAttribute('type', data.mimeType);
        else if ('mimeType' in data) sources[i].removeAttribute('type');
      }
    }
  }

  function rectOf(el, scrollY) {
    if (!el || !el.getBoundingClientRect) return null;
    var r = el.getBoundingClientRect();
    return {
      x: r.x,
      y: r.y + (scrollY || 0),
      width: r.width,
      height: r.height
    };
  }

  function cssPx(value) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
  }

  function rectOfContentBox(el, scrollY) {
    var rect = rectOf(el, scrollY);
    if (!rect) return null;
    var style = getComputedStyle(el);
    var left = cssPx(style.paddingLeft);
    var right = cssPx(style.paddingRight);
    var top = cssPx(style.paddingTop);
    var bottom = cssPx(style.paddingBottom);
    return {
      x: rect.x + left,
      y: rect.y + top,
      width: Math.max(0, rect.width - left - right),
      height: Math.max(0, rect.height - top - bottom)
    };
  }

  function unionRects(rects) {
    var usable = rects.filter(function(rect) {
      return rect && rect.width && rect.height;
    });
    if (!usable.length) return null;
    var left = usable[0].x;
    var top = usable[0].y;
    var right = usable[0].x + usable[0].width;
    var bottom = usable[0].y + usable[0].height;
    for (var i = 1; i < usable.length; i++) {
      var rect = usable[i];
      left = Math.min(left, rect.x);
      top = Math.min(top, rect.y);
      right = Math.max(right, rect.x + rect.width);
      bottom = Math.max(bottom, rect.y + rect.height);
    }
    return {
      x: left,
      y: top,
      width: right - left,
      height: bottom - top
    };
  }

  function rectOfLogoSlot(slot, wrapper, scrollY) {
    var slotRect = rectOf(slot, scrollY);
    if (slotRect && slotRect.width && slotRect.height) return slotRect;
    var childRects = [];
    if (slot) {
      var children = slot.querySelectorAll('img, svg, picture');
      for (var i = 0; i < children.length; i++) {
        childRects.push(rectOf(children[i], scrollY));
      }
    }
    return unionRects(childRects) || rectOf(wrapper, scrollY);
  }

  function findLogoBoundary(logoWrapper) {
    var outer = logoWrapper.closest('#navigation, #footer, header, footer, nav, [data-pb-instance]');
    var selectors = '.cs-container, .container, .nav-container, .navbar-container, .cs-top-container, .cs-bottom-container, .left-section, .right-section, .cs-logo-group, .logo-group';
    var current = logoWrapper.parentElement;
    while (current && current !== document.body) {
      if (current.matches && current.matches(selectors)) return current;
      if (current === outer) break;
      current = current.parentElement;
    }
    return outer || logoWrapper.parentElement;
  }

  function isUsableLogoBlocker(blocker, logoWrapper) {
    if (!blocker || blocker === logoWrapper) return false;
    if (logoWrapper.contains(blocker) || blocker.contains(logoWrapper)) return false;
    var style = getComputedStyle(blocker);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
    return true;
  }

  function readLogoVars(slot) {
    if (!slot) return null;
    var style = getComputedStyle(slot);
    var breakpoint = readLogoBreakpoint();
    var responsiveName = function(base) {
      return base + '-' + breakpoint;
    };
    var readNumber = function(name, fallback) {
      var parsed = parseFloat(style.getPropertyValue(name));
      return isFinite(parsed) ? parsed : fallback;
    };
    var readResponsiveNumber = function(base, fallback) {
      var scoped = parseFloat(style.getPropertyValue(responsiveName(base)));
      if (isFinite(scoped)) return scoped;
      return readNumber(base, fallback);
    };
    var readResponsiveValue = function(base) {
      return (style.getPropertyValue(responsiveName(base)) || style.getPropertyValue(base) || '').trim();
    };
    return {
      tx: readResponsiveNumber('--pb-logo-tx', 0),
      ty: readResponsiveNumber('--pb-logo-ty', 0),
      scale: readResponsiveNumber('--pb-logo-scale', 1),
      slotWidth: readResponsiveValue('--pb-logo-slot-width'),
      slotHeight: readResponsiveValue('--pb-logo-slot-height'),
      // Not breakpoint-scoped: one asset, one set of proportions.
      aspectRatio: (style.getPropertyValue('--pb-logo-aspect-ratio') || '').trim(),
      breakpoint: breakpoint
    };
  }

  function readLogoBreakpoint() {
    var width = Number(window.innerWidth || document.documentElement.clientWidth || 0);
    if (width > 0 && width < 768) return 'mobile';
    if (width > 0 && width < 1024) return 'tablet';
    return 'desktop';
  }

  function addLogoRectPayload(payload, logoWrapper, scrollY) {
    if (!payload || !logoWrapper || logoWrapper.getAttribute('data-pb-editable') !== 'logo') return payload;
    var slot = logoWrapper.querySelector('.logo-slot');
    var boundary = findLogoBoundary(logoWrapper);
    var blockers = [];
    if (boundary) {
      var seenBlockers = [];
      var addBlocker = function(blocker) {
        if (!isUsableLogoBlocker(blocker, logoWrapper) || seenBlockers.indexOf(blocker) !== -1) return;
        seenBlockers.push(blocker);
        var b = rectOf(blocker, scrollY);
        if (b && b.width && b.height) blockers.push(b);
      };
      var blockerEls = boundary.querySelectorAll('#navbar-menu, .hamburger-menu, .header-actions, .credit, .right-section, .cs-toggle, .cs-nav, .cs-ul-wrapper, .cs-contact-group, .cs-button-solid, .cs-nav-button, #dark-mode-toggle, nav, button, [role="navigation"]');
      for (var bi = 0; bi < blockerEls.length; bi++) {
        addBlocker(blockerEls[bi]);
      }
      for (var child = boundary.firstElementChild; child; child = child.nextElementSibling) {
        addBlocker(child);
      }
    }
    payload.logoSlotRect = slot ? rectOfLogoSlot(slot, logoWrapper, scrollY) : null;
    payload.logoBoundaryRect = boundary ? rectOfContentBox(boundary, scrollY) : null;
    payload.logoBlockerRects = blockers;
    payload.logoVars = readLogoVars(slot);
    payload.logoEngaged = slot ? slot.getAttribute('data-logo-engaged') === 'true' : false;
    payload.logoBreakpoint = payload.logoVars ? payload.logoVars.breakpoint : readLogoBreakpoint();
    return payload;
  }

  function isTextEditable(el) {
    return !!(el && el.getAttribute('data-pb-editable') === 'text');
  }

  function isTypingTarget(el) {
    if (!el) return false;
    var tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return !!el.isContentEditable;
  }

  function isTextEditableEmpty(el) {
    if (!isTextEditable(el)) return false;
    var text = (el.textContent || '').split(String.fromCharCode(160)).join(' ').trim();
    return text.length === 0;
  }

  function syncTextEditablePlaceholder(el) {
    if (!isTextEditable(el)) return;
    if (isTextEditableEmpty(el)) el.classList.add('pb-edit-empty');
    else el.classList.remove('pb-edit-empty');
  }

  function _countEditableChars(el) {
    if (!el) return 0;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length;
  }

  function _updateEditingPill(el) {
    if (!isTextEditable(el)) return;
    var tag = (el.tagName || 'TEXT').toUpperCase();
    var count = _countEditableChars(el);
    var unit = count === 1 ? 'char' : 'chars';
    _stampPillSlot(el);
    el.setAttribute('data-pb-edit-pill', tag + ' · ' + count + ' ' + unit);
  }

  function _clearEditingPill(el) {
    if (!el) return;
    el.removeAttribute('data-pb-edit-pill');
  }

  /*
   * Editable target / pill host audit.
   *
   * data-pb-editable value | typical target tags            | host resolution        | parent overlay fallback resolution
   * text                  | H1-H6, P, LI, A, SPAN, DIV    | self-host              | none; iframe selected/hover/edit pills own it
   * button                | A, BUTTON, form button inputs | self-host              | none; iframe selected/hover pills own it
   * container             | LI/DIV card wrappers          | self-host              | none; iframe selected/hover pills own it
   * logo                  | A.logo/A.cs-logo wrappers     | self-host              | none; logo media remains inside the wrapper
   * image                 | IMG                           | wrapper-host or marker | marker-host keeps selected in iframe; hover may fall back to parent
   * svg                   | SVG                           | wrapper-host or marker | marker-host keeps selected in iframe; hover may fall back to parent
   *
   * The editable target always keeps data-pb-editable/data-pb-eid. Replaced
   * elements get a separate pill host because ::after does not render
   * reliably on IMG/SVG/INPUT/VIDEO across browsers.
   */

  /*
   * Pseudo-element slot arbitration.
   *
   * The pill CSS forces position/margin/width on the host's generated box, so
   * claiming a pseudo-element the template already uses does not just repaint
   * it — it removes a real in-flow box. CodeStitch top bars build their column
   * dividers as '.cs-item:after { content:''; width:1px; margin:0 clamp(...) }',
   * so hovering an item deleted ~90px of flex track and every item before it
   * slid right (the last item was exempt: ':last-of-type:after {display:none}').
   *
   * Slots resolve to 'after' (free — no attribute stamped, historical default),
   * 'before' (::after taken, pill moves to ::before) or 'none' (both taken —
   * no iframe pill, the parent overlay's element tag takes over).
   */
  var _pillSlotCache = new WeakMap();
  var _pseudoStyleSupport = null;

  /* jsdom (and any engine without pseudo-element computed styles) answers every
   * query with the element's own style, which would read as "occupied" on every
   * host and silently disable pills everywhere. Probe head::after — nothing
   * styles it — and require the spec answer for an unstyled pseudo before
   * trusting any measurement. */
  function _pseudoStylesReadable() {
    if (_pseudoStyleSupport !== null) return _pseudoStyleSupport;
    _pseudoStyleSupport = false;
    try {
      var probe = window.getComputedStyle(document.head || document.documentElement, '::after');
      _pseudoStyleSupport = !!probe && probe.content === 'none';
    } catch (err) {
      _pseudoStyleSupport = false;
    }
    return _pseudoStyleSupport;
  }

  function _pseudoIsOccupied(el, pseudo) {
    if (!_pseudoStylesReadable()) return false;
    try {
      var computed = window.getComputedStyle(el, pseudo);
      if (!computed) return false;
      if (computed.display === 'none') return false;
      var content = computed.content;
      // '' is never a browser's answer for a generated box: an absent pseudo
      // computes to 'none', and an empty content value serializes with quotes.
      if (!content) return false;
      return content !== 'none' && content !== 'normal';
    } catch (err) {
      return false;
    }
  }

  function _hostCarriesPill(el) {
    if (!el || !el.hasAttribute) return false;
    return el.hasAttribute('data-pb-hover-label')
      || el.hasAttribute('data-pb-selected-label')
      || el.hasAttribute('data-pb-edit-pill');
  }

  function _pillSlotFor(el) {
    if (!el || el.nodeType !== 1) return 'after';
    if (el.matches && el.matches('.pb-pill-marker')) return 'after';
    var cached = _pillSlotCache.get(el);
    if (cached) return cached;
    // Measuring a host that already carries a label would read the builder's
    // own pill back as template content. Every editable is measured on the sync
    // pass before any label is set, so an uncached labelled host is an unusual
    // path — keep it on the historical slot rather than guess.
    if (_hostCarriesPill(el)) return 'after';
    var slot = 'after';
    if (_pseudoIsOccupied(el, '::after')) {
      slot = _pseudoIsOccupied(el, '::before') ? 'none' : 'before';
    }
    _pillSlotCache.set(el, slot);
    return slot;
  }

  function _stampPillSlot(host) {
    var slot = _pillSlotFor(host);
    if (!host || !host.setAttribute) return slot;
    if (slot === 'after') host.removeAttribute('data-pb-pill-slot');
    else host.setAttribute('data-pb-pill-slot', slot);
    return slot;
  }

  function _isReplacedPillTarget(el) {
    var tag = (el && el.tagName ? el.tagName : '').toUpperCase();
    return tag === 'IMG' || tag === 'SVG' || tag === 'IFRAME' || tag === 'INPUT' || tag === 'VIDEO' || tag === 'CANVAS';
  }

  function _isNaturalPillHost(target, candidate) {
    if (!target || !candidate || candidate === target) return false;
    if (!candidate.matches || candidate.matches('[data-pb-instance], [data-pb-section], body, html')) return false;
    if (candidate.hasAttribute && candidate.hasAttribute('data-pb-editable')) return false;
    var tag = (candidate.tagName || '').toUpperCase();
    if (tag === 'PICTURE' || tag === 'FIGURE' || tag === 'A') return true;
    var cls = typeof candidate.className === 'string' ? candidate.className.toLowerCase() : '';
    return /(?:^|\s)(?:cs-image|cs-picture|cs-media|cs-photo|cs-img|cs-icon|cs-graphic|cs-logo|logo-slot|image|picture|media|photo|avatar|icon|graphic)(?:\s|$|-|_)/.test(cls);
  }

  function _findNaturalPillHost(el) {
    if (!el || !el.parentElement) return null;
    var wrapper = el.closest ? el.closest('[data-pb-instance]') : null;
    var current = el.parentElement;
    while (current && current !== document.body && current !== wrapper) {
      if (_isNaturalPillHost(el, current)) return current;
      current = current.parentElement;
    }
    return null;
  }

  function _markerSelectorForEid(eid) {
    return '.pb-pill-marker[data-pb-marker-for-eid="' + _escapeSelectorValue(eid || '') + '"]';
  }

  function _positionPillMarker(marker, target) {
    if (!marker || !target || !target.getBoundingClientRect) return;
    var rect = target.getBoundingClientRect();
    var offsetParent = marker.offsetParent || document.body;
    var parentRect = offsetParent && offsetParent.getBoundingClientRect
      ? offsetParent.getBoundingClientRect()
      : { left: 0, top: -window.scrollY };
    var left = rect.left + window.scrollX - (parentRect.left + window.scrollX);
    var top = rect.top + window.scrollY - (parentRect.top + window.scrollY);
    marker.style.left = left + 'px';
    marker.style.top = top + 'px';
    marker.style.width = Math.max(0, rect.width || 0) + 'px';
    marker.style.height = Math.max(0, rect.height || 0) + 'px';
  }

  function _ensurePillMarkerFor(el) {
    if (!el || !el.parentNode) return null;
    var eid = el.getAttribute('data-pb-eid') || '';
    var marker = null;
    if (eid && el.parentElement && el.parentElement.querySelector) {
      marker = el.parentElement.querySelector(_markerSelectorForEid(eid));
    }
    if (!marker) {
      marker = document.createElement('span');
      marker.className = 'pb-pill-marker';
      marker.setAttribute('aria-hidden', 'true');
      // Template rules often target every span with !important. Keep this
      // transient marker absolutely positioned so it never becomes a flex/grid
      // item or creates a phantom gap beside the selected media element.
      marker.style.setProperty('position', 'absolute', 'important');
      if (eid) marker.setAttribute('data-pb-marker-for-eid', eid);
      el.parentNode.insertBefore(marker, el.nextSibling);
    }
    _positionPillMarker(marker, el);
    return marker;
  }

  function _resolveSelectedStateHost(el) {
    if (!el) return { host: null, requiresParentPill: false, requiresParentHoverPill: false, kind: 'none' };
    if (!_isReplacedPillTarget(el)) {
      // Template owns both pseudo-elements: there is no slot to borrow without
      // deleting one of its boxes, so hand the hover tag to the parent overlay
      // and let the selection outline carry selection on its own.
      if (_pillSlotFor(el) === 'none') {
        return { host: el, requiresParentPill: true, requiresParentHoverPill: true, kind: 'parent' };
      }
      return { host: el, requiresParentPill: false, requiresParentHoverPill: false, kind: 'self' };
    }
    var wrapperHost = _findNaturalPillHost(el);
    if (wrapperHost) {
      return { host: wrapperHost, requiresParentPill: false, requiresParentHoverPill: false, kind: 'wrapper' };
    }
    var markerHost = _ensurePillMarkerFor(el);
    return {
      host: markerHost,
      requiresParentPill: !markerHost,
      requiresParentHoverPill: !!markerHost,
      kind: markerHost ? 'marker' : 'parent'
    };
  }

  function _clearPillHostAttrs(host) {
    if (!host || !host.removeAttribute) return;
    host.removeAttribute('data-pb-selected-label');
    host.removeAttribute('data-pb-selected-for-eid');
    _syncPillHostPositionAnchor(host);
  }

  function _hostNeedsPositionAnchor(host) {
    if (!host || !host.matches || host.matches('.pb-pill-marker')) return false;
    try {
      var position = getComputedStyle(host).position;
      return !position || position === 'static';
    } catch (err) {
      return true;
    }
  }

  // A pill is drawn 8px above its host and stands ~20px tall. A host within that
  // of the canvas frame's top edge has its nameplate painted off-viewport and
  // sliced in half, so it is flipped underneath instead. CSS cannot see the
  // viewport edge; this is the measurement that lets it.
  //
  // Deliberately NOT called from _syncPillHostPositionAnchor, which runs inside
  // the bulk _syncEditableDisplayAttributes pass: data-pb-hover-label is stamped
  // on EVERY editable, so hanging a getBoundingClientRect off that pass would
  // force one layout per editable, interleaved with the label writes — exactly
  // the thrash the slot-cache warm-up above exists to avoid. The two reporters
  // call it instead: they run for ONE element, one that they have already
  // measured, and they re-run on scroll, resize and mutation.
  var PILL_CLEARANCE_CSS_PX = 30;

  function _syncPillHostFlip(host) {
    if (!host || !host.setAttribute || !host.removeAttribute) return;
    if (!host.getBoundingClientRect) {
      host.removeAttribute('data-pb-pill-flip');
      return;
    }
    try {
      // getBoundingClientRect answers in VISUAL pixels under canvas zoom, while
      // the clearance is a CSS length on the pill — scale before comparing or a
      // zoomed-out canvas flips pills that had room.
      var zoom = getCurrentZoom();
      var clearance = PILL_CLEARANCE_CSS_PX * (isFinite(zoom) && zoom > 0 ? zoom : 1);
      var top = host.getBoundingClientRect().top;
      if (top < clearance) host.setAttribute('data-pb-pill-flip', 'below');
      else host.removeAttribute('data-pb-pill-flip');
    } catch (err) {
      host.removeAttribute('data-pb-pill-flip');
    }
  }

  function _syncPillHostPositionAnchor(host) {
    if (!host || !host.setAttribute || !host.removeAttribute) return;
    var hasPillLabel = !!(
      (host.hasAttribute && host.hasAttribute('data-pb-selected-label'))
      || (host.hasAttribute && host.hasAttribute('data-pb-hover-label'))
    );
    if (hasPillLabel && (host.hasAttribute('data-pb-label-position-anchor') || _hostNeedsPositionAnchor(host))) {
      host.setAttribute('data-pb-label-position-anchor', 'true');
    } else {
      host.removeAttribute('data-pb-label-position-anchor');
    }
  }

  function _setPillHostLabel(host, attr, value) {
    if (!host || !host.setAttribute) return;
    // Stamp before the label lands: once the host carries a pill attribute its
    // own generated content is what getComputedStyle would report back.
    _stampPillSlot(host);
    host.setAttribute(attr, value);
    _syncPillHostPositionAnchor(host);
  }

  function _clearHoverPillHost(host) {
    if (!host || !host.removeAttribute) return;
    host.removeAttribute('data-pb-hover-label');
    _syncPillHostPositionAnchor(host);
  }

  function _updateSelectedState(el) {
    if (!el || !el.setAttribute) return;
    el.setAttribute('data-pb-selected-target', 'true');
    var resolved = _resolveSelectedStateHost(el);
    if (!resolved || !resolved.host || resolved.requiresParentPill) return;
    var label = _editableDisplayLabel(el, el.getAttribute('data-pb-editable') || '');
    var eid = el.getAttribute('data-pb-eid') || '';
    _setPillHostLabel(resolved.host, 'data-pb-selected-label', label);
    resolved.host.setAttribute('data-pb-selected-for-eid', eid);
    // One element, at the moment its nameplate appears — so the flip is right on
    // the first painted frame rather than a rAF later. reportElementRect keeps it
    // true afterwards. Not cleared on deselect: the attribute generates nothing
    // on a host showing no pill, and a host that IS still hovered is still where
    // it was, so clearing would only cost a frame of flicker.
    _syncPillHostFlip(resolved.host);
    if (resolved.kind === 'marker') _positionPillMarker(resolved.host, el);
  }

  function _clearSelectedState(oldEl) {
    if (!oldEl) return;
    if (oldEl.removeAttribute) oldEl.removeAttribute('data-pb-selected-target');
    var resolved = _resolveSelectedStateHost(oldEl);
    if (!resolved || !resolved.host) return;
    var stampedEid = resolved.host.getAttribute('data-pb-selected-for-eid');
    var targetEid = oldEl.getAttribute ? oldEl.getAttribute('data-pb-eid') : '';
    if (stampedEid && targetEid && stampedEid !== targetEid) return;
    _clearPillHostAttrs(resolved.host);
  }

  function _setSelectedEditable(nextEditable) {
    if (_selectedEditable === nextEditable) {
      _updateSelectedState(_selectedEditable);
      observeSelectedEditable(_selectedEditable);
      return;
    }
    _clearSelectedState(_selectedEditable);
    _selectedEditable = nextEditable || null;
    _updateSelectedState(_selectedEditable);
    observeSelectedEditable(_selectedEditable);
  }

  function _syncEditableDisplayAttributes(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var editables = [];
    if (scope.matches && scope.matches('[data-pb-editable]')) {
      editables.push(scope);
    }
    var descendants = scope.querySelectorAll ? scope.querySelectorAll('[data-pb-editable]') : [];
    for (var di = 0; di < descendants.length; di++) {
      editables.push(descendants[di]);
    }
    // Warm the slot cache in one read-only pass. Interleaving getComputedStyle
    // with the label writes below would force a style recalc per editable.
    for (var si = 0; si < editables.length; si++) _pillSlotFor(editables[si]);
    for (var i = 0; i < editables.length; i++) {
      var editable = editables[i];
      var resolved = _resolveSelectedStateHost(editable);
      if (!resolved || !resolved.host || resolved.requiresParentPill) continue;
      if (resolved.kind === 'marker') {
        _clearHoverPillHost(resolved.host);
        continue;
      }
      _setPillHostLabel(resolved.host, 'data-pb-hover-label', _editableDisplayLabel(editable, editable.getAttribute('data-pb-editable') || ''));
    }
  }

  function syncAllTextEditablePlaceholders() {
    var textEditables = document.querySelectorAll('[data-pb-editable="text"]');
    for (var i = 0; i < textEditables.length; i++) {
      syncTextEditablePlaceholder(textEditables[i]);
    }
  }

  function _nextEditSessionId() {
    var id = _editSessionCounter;
    _editSessionCounter += 1;
    return id;
  }

  function _getNodePath(root, node) {
    if (!root || !node) return null;
    var path = [];
    var current = node;
    while (current && current !== root) {
      var parent = current.parentNode;
      if (!parent) return null;
      var index = Array.prototype.indexOf.call(parent.childNodes, current);
      if (index < 0) return null;
      path.unshift(index);
      current = parent;
    }
    return current === root ? path : null;
  }

  function _resolveNodePath(root, path) {
    if (!root || !Array.isArray(path)) return null;
    var current = root;
    for (var i = 0; i < path.length; i++) {
      var index = path[i];
      if (!current || !current.childNodes || index < 0 || index >= current.childNodes.length) {
        return null;
      }
      current = current.childNodes[index];
    }
    return current;
  }

  function _clampOffset(node, offset) {
    if (!node || typeof offset !== 'number') return 0;
    var max = node.nodeType === 3
      ? (node.textContent ? node.textContent.length : 0)
      : (node.childNodes ? node.childNodes.length : 0);
    if (offset < 0) return 0;
    if (offset > max) return max;
    return offset;
  }

  function captureSelectionSnapshot(editable) {
    if (!editable) return null;
    var sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    var range = sel.getRangeAt(0);
    try {
      if (!editable.contains(range.startContainer) || !editable.contains(range.endContainer)) return null;
    } catch (e) {
      return null;
    }

    var startPath = _getNodePath(editable, range.startContainer);
    var endPath = _getNodePath(editable, range.endContainer);
    if (!startPath || !endPath) return null;

    return {
      eid: editable.getAttribute('data-pb-eid') || null,
      startPath: startPath,
      startOffset: range.startOffset,
      endPath: endPath,
      endOffset: range.endOffset
    };
  }

  function restoreSelectionSnapshot(editable, snapshot) {
    if (!editable || !snapshot) return false;
    if (snapshot.eid && snapshot.eid !== editable.getAttribute('data-pb-eid')) return false;

    var startNode = _resolveNodePath(editable, snapshot.startPath);
    var endNode = _resolveNodePath(editable, snapshot.endPath);
    if (!startNode || !endNode) return false;

    var range = document.createRange();
    try {
      range.setStart(startNode, _clampOffset(startNode, snapshot.startOffset));
      range.setEnd(endNode, _clampOffset(endNode, snapshot.endOffset));
    } catch (e) {
      return false;
    }

    var sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    lastRange = range.cloneRange();
    return true;
  }

  function captureRangeSnapshot(editable, range) {
    if (!editable || !range) return null;
    try {
      if (!editable.contains(range.startContainer) || !editable.contains(range.endContainer)) return null;
    } catch (e) {
      return null;
    }
    var startPath = _getNodePath(editable, range.startContainer);
    var endPath = _getNodePath(editable, range.endContainer);
    if (!startPath || !endPath) return null;
    return {
      eid: editable.getAttribute('data-pb-eid') || null,
      startPath: startPath,
      startOffset: range.startOffset,
      endPath: endPath,
      endOffset: range.endOffset
    };
  }

  function restoreRangeSnapshot(editable, snapshot) {
    if (!editable || !snapshot) return null;
    if (snapshot.eid && snapshot.eid !== editable.getAttribute('data-pb-eid')) return null;
    var startNode = _resolveNodePath(editable, snapshot.startPath);
    var endNode = _resolveNodePath(editable, snapshot.endPath);
    if (!startNode || !endNode) return null;
    var range = document.createRange();
    try {
      range.setStart(startNode, _clampOffset(startNode, snapshot.startOffset));
      range.setEnd(endNode, _clampOffset(endNode, snapshot.endOffset));
    } catch (e) {
      return null;
    }
    return range;
  }

  function _activeEditableEid() {
    return activeEditable ? (activeEditable.getAttribute('data-pb-eid') || null) : null;
  }

  function _focusActiveEditable() {
    if (!activeEditable) return;
    var activeElement = document.activeElement;
    if (activeElement === activeEditable || (activeElement && activeEditable.contains(activeElement))) return;
    try {
      activeEditable.focus({ preventScroll: true });
    } catch (focusErr) {
      try { activeEditable.focus(); } catch (ignoreFocusErr) {}
    }
  }

  function applyEditingAria(target) {
    if (!target) return;
    var tag = (target.tagName || '').toLowerCase();
    var visibleText = (target.textContent || '').replace(/\s+/g, ' ').trim();
    if (visibleText.length > 40) visibleText = visibleText.slice(0, 40) + '...';
    var label = 'Editing: ' + tag + (visibleText ? ' - ' + visibleText : '');
    target.setAttribute('role', 'textbox');
    target.setAttribute('aria-label', label);
    if (!/^H[1-6]$/.test(target.tagName || '') && target.tagName !== 'SPAN') {
      target.setAttribute('aria-multiline', 'true');
    } else {
      target.removeAttribute('aria-multiline');
    }
  }

  function clearEditingAria(target) {
    if (!target) return;
    target.removeAttribute('role');
    target.removeAttribute('aria-label');
    target.removeAttribute('aria-multiline');
  }

  function addComputedStyleMeta(path, el, style, rootStyle) {
    if (!path || !style) return;
    path.tagName = el.tagName || '';
    path.computedRootFontSize = (rootStyle && rootStyle.fontSize) || '16px';
    path.computedTextAlign = style.textAlign || '';
    path.computedFontFamily = style.fontFamily || '';
    path.computedFontSize = style.fontSize || '';
    var authoredFontSize = getAuthoredStyleValue(el, 'font-size');
    var customPropertyMatch = authoredFontSize.match(/^var\(\s*(--[A-Za-z0-9_-]+)/);
    if (customPropertyMatch && typeof style.getPropertyValue === 'function') {
      var customPropertyValue = String(style.getPropertyValue(customPropertyMatch[1]) || '').trim();
      if (customPropertyValue) authoredFontSize = customPropertyValue;
    }
    path.authoredFontSize = authoredFontSize || path.computedFontSize;
    path.computedFontWeight = style.fontWeight || '';
    path.computedColor = style.color || '';
    path.computedBackgroundColor = style.backgroundColor || '';
    path.computedBorderColor = style.borderColor || '';
    path.computedBorderStyle = style.borderStyle || '';
    path.computedBorderWidth = style.borderWidth || '';
    path.computedBorderRadius = style.borderRadius || '';
    path.computedPadding = style.padding || '';
    path.computedDisplay = style.display || '';
    path.computedWidth = style.width || '';
    path.computedHeight = style.height || '';
    path.computedMaxWidth = style.maxWidth || '';
    path.computedMarginLeft = style.marginLeft || '';
    path.computedMarginRight = style.marginRight || '';
    path.computedJustifyContent = style.justifyContent || '';
    path.computedAlignItems = style.alignItems || '';
    path.computedObjectFit = style.objectFit || '';
    path.computedAspectRatio = style.aspectRatio || '';
    path.computedBoxShadow = style.boxShadow || '';
  }

  // ── StyleSnapshot: element's own computed style + iframe-resolved token identity ──

  function _pbParseRgb(str) {
    if (!str) return null;
    var text = String(str).trim();
    var srgb = text.match(/^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.%]+))?\)$/i);
    if (srgb) {
      var srgbAlpha = srgb[4] && srgb[4].indexOf('%') !== -1
        ? parseFloat(srgb[4]) / 100
        : parseFloat(srgb[4] || '1');
      return {
        r: Math.round(parseFloat(srgb[1]) * 255),
        g: Math.round(parseFloat(srgb[2]) * 255),
        b: Math.round(parseFloat(srgb[3]) * 255),
        a: isNaN(srgbAlpha) ? 1 : srgbAlpha
      };
    }
    var m = text.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    var body = m[1].replace(/\s*\/\s*/, ',');
    var parts = body.indexOf(',') !== -1 ? body.split(',') : body.trim().split(/\s+/);
    if (parts.length < 3) return null;
    function channel(value) {
      return String(value).indexOf('%') !== -1 ? parseFloat(value) * 2.55 : parseFloat(value);
    }
    var r = channel(parts[0]), g = channel(parts[1]), b = channel(parts[2]);
    var a = parts.length >= 4
      ? (String(parts[3]).indexOf('%') !== -1 ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]))
      : 1;
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: isNaN(a) ? 1 : a };
  }

  function _pbRgbEqual(a, b) {
    if (!a || !b) return false;
    var aa = (a.a == null ? 1 : a.a), ba = (b.a == null ? 1 : b.a);
    return Math.abs(a.r - b.r) <= 1 && Math.abs(a.g - b.g) <= 1 &&
           Math.abs(a.b - b.b) <= 1 && Math.abs(aa - ba) <= 0.01;
  }

  function _pbRgbToHex(rgb) {
    if (!rgb) return '';
    function h(n) { var s = Math.max(0, Math.min(255, n)).toString(16); return s.length === 1 ? '0' + s : s; }
    var base = '#' + h(rgb.r) + h(rgb.g) + h(rgb.b);
    return rgb.a < 0.999 ? base + h(Math.round(rgb.a * 255)) : base;
  }

  // Effective background behind an element's text: walk from the element up,
  // compositing every translucent background-color layer in paint order over
  // the first opaque one (page white at the root). Any paint effect the walk
  // cannot model — background images/gradients, reduced opacity, blend modes,
  // filters, masks, or a painted ::before/::after overlay — yields
  // { known:false, reason } instead of a guessed color. The element's OWN
  // background-color counts as the nearest layer (text paints over it).
  function _pbEffectiveBackground(el) {
    if (!el || !el.ownerDocument) return { known: false, reason: 'no-element' };
    function compose(overRgba, underRgb) {
      var a = overRgba.a == null ? 1 : overRgba.a;
      function mix(over, under) { return Math.round(over * a + under * (1 - a)); }
      return { r: mix(overRgba.r, underRgb.r), g: mix(overRgba.g, underRgb.g), b: mix(overRgba.b, underRgb.b) };
    }
    function paintedPseudo(node, which) {
      var ps;
      try { ps = getComputedStyle(node, which); } catch (e) { return false; }
      if (!ps || !ps.content || ps.content === 'none' || ps.display === 'none') return false;
      if (ps.backgroundImage && ps.backgroundImage !== 'none') return true;
      var bg = _pbParseRgb(ps.backgroundColor);
      return !!(bg && bg.a > 0.001);
    }
    var layers = [];
    var node = el;
    var doc = el.ownerDocument;
    while (node && node.nodeType === 1) {
      var style = getComputedStyle(node);
      if (style.backgroundImage && style.backgroundImage !== 'none')
        return { known: false, reason: 'background-image' };
      if (parseFloat(style.opacity) < 1)
        return { known: false, reason: 'opacity' };
      if (style.mixBlendMode && style.mixBlendMode !== 'normal')
        return { known: false, reason: 'blend-mode' };
      if ((style.filter && style.filter !== 'none') ||
          (style.backdropFilter && style.backdropFilter !== 'none'))
        return { known: false, reason: 'filter' };
      var maskImage = style.maskImage || style.webkitMaskImage;
      if (maskImage && maskImage !== 'none')
        return { known: false, reason: 'mask' };
      if (paintedPseudo(node, '::before') || paintedPseudo(node, '::after'))
        return { known: false, reason: 'pseudo-overlay' };
      var bg = _pbParseRgb(style.backgroundColor);
      if (bg && bg.a > 0.001) {
        if (bg.a >= 0.999) {
          var color = { r: bg.r, g: bg.g, b: bg.b };
          // Collected nearest-first; paint order applies farthest first.
          for (var i = layers.length - 1; i >= 0; i--) color = compose(layers[i], color);
          return {
            known: true,
            rgb: color,
            hex: _pbRgbToHex({ r: color.r, g: color.g, b: color.b, a: 1 }),
            sourceEid: node.getAttribute ? (node.getAttribute('data-pb-eid') || '') : ''
          };
        }
        layers.push(bg);
      }
      if (node === doc.documentElement) break;
      node = node.parentElement;
    }
    // No opaque layer up to the root: the page canvas paints white.
    var base = { r: 255, g: 255, b: 255 };
    for (var j = layers.length - 1; j >= 0; j--) base = compose(layers[j], base);
    return {
      known: true,
      rgb: base,
      hex: _pbRgbToHex({ r: base.r, g: base.g, b: base.b, a: 1 }),
      sourceEid: ''
    };
  }

  // Probe each candidate token INSIDE the element's own cascade context so
  // section-scoped var redefinitions, body.dark-mode, and currentColor resolve
  // correctly. Returns { '--token': rgbaObj }. The probe is created/read/removed
  // in one synchronous pass and carries data-pb-ignore so observers skip it.
  function _pbBuildTokenColorMap(el, tokenNames) {
    var map = {};
    if (!el || !el.ownerDocument || !tokenNames || !tokenNames.length) return map;
    var probe = el.ownerDocument.createElement('span');
    probe.setAttribute('data-pb-ignore', '');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;visibility:hidden;pointer-events:none;';
    el.appendChild(probe);
    try {
      var SENTINEL = 'rgba(1, 2, 3, 0.937)';
      for (var i = 0; i < tokenNames.length; i++) {
        var t = tokenNames[i];
        if (typeof t !== 'string' || t.indexOf('--') !== 0) continue;
        probe.style.color = 'var(' + t + ', ' + SENTINEL + ')';
        var rgb = _pbParseRgb(getComputedStyle(probe).color);
        // Undefined token → resolves to the sentinel; treat as "no value".
        if (rgb && !(rgb.r === 1 && rgb.g === 2 && rgb.b === 3)) map[t] = rgb;
      }
    } finally {
      if (probe.parentNode) probe.parentNode.removeChild(probe);
    }
    return map;
  }

  // tokenNames is priority-ordered, so the first exact match is the canonical
  // winner; all exact matches are recorded in \`ambiguous\`. No nearest-match —
  // anything not exactly equal is a custom color.
  function _pbMatchToken(colorMap, tokenNames, targetStr) {
    var target = _pbParseRgb(targetStr);
    var out = { token: null, hex: _pbRgbToHex(target), rgba: targetStr || '', exact: false, ambiguous: [], custom: true };
    if (!target) return out;
    var matches = [];
    for (var i = 0; i < tokenNames.length; i++) {
      var t = tokenNames[i];
      if (colorMap[t] && _pbRgbEqual(colorMap[t], target)) matches.push(t);
    }
    if (matches.length) {
      out.token = matches[0];
      out.exact = true;
      out.custom = false;
      out.ambiguous = matches;
    }
    return out;
  }

  // Resolve intended override values in the element's own cascade context
  // (Phase 6 apply-and-verify): a probe span resolves e.g. 'var(--primary)' or
  // '1.125rem' to the computed value the override SHOULD produce, so the
  // parent can compare intent vs. the element's actual computed value without
  // guessing at var()/rem resolution. Keys are camelCase style props.
  function _pbResolveIntendedValues(el, valueMap) {
    var out = {};
    if (!el || !el.ownerDocument || !valueMap) return out;
    var probe = el.ownerDocument.createElement('span');
    probe.setAttribute('data-pb-ignore', '');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;visibility:hidden;pointer-events:none;';
    el.appendChild(probe);
    try {
      for (var key in valueMap) {
        if (!Object.prototype.hasOwnProperty.call(valueMap, key)) continue;
        var v = valueMap[key];
        if (typeof v !== 'string' || v === '') continue;
        try {
          probe.style[key] = v;
          out[key] = String(getComputedStyle(probe)[key] || '');
        } catch (e) { /* unknown prop/value: leave unresolved */ }
      }
    } finally {
      if (probe.parentNode) probe.parentNode.removeChild(probe);
    }
    return out;
  }

  function buildStyleSnapshot(el, data) {
    data = data || {};
    var style = getComputedStyle(el);
    var rootStyle = getComputedStyle(document.documentElement);
    var authoredFontSize = getAuthoredStyleValue(el, 'font-size') || style.fontSize || '';
    var authoredVar = authoredFontSize.match(/^var\(\s*(--[A-Za-z0-9_-]+)/);
    if (authoredVar && typeof style.getPropertyValue === 'function') {
      var authoredVarValue = String(style.getPropertyValue(authoredVar[1]) || '').trim();
      if (authoredVarValue) authoredFontSize = authoredVarValue;
    }
    var tokenNames = (data.candidateTokens && data.candidateTokens.length)
      ? data.candidateTokens
      : (typeof __PB_STYLE_TOKENS__ !== 'undefined' ? __PB_STYLE_TOKENS__ : []);
    var colorMap = _pbBuildTokenColorMap(el, tokenNames);
    var parentEl = el.parentElement;
    var parentColor = parentEl ? getComputedStyle(parentEl).color : '';
    var rootTokens = {};
    for (var k = 0; k < tokenNames.length; k++) {
      rootTokens[tokenNames[k]] = colorMap[tokenNames[k]] ? _pbRgbToHex(colorMap[tokenNames[k]]) : '';
    }
    return {
      instanceId: data.instanceId || '',
      eid: data.eid || '',
      editableType: el.getAttribute('data-pb-editable') || '',
      requestId: (data.requestId != null ? data.requestId : null),
      reason: data.reason || 'click',
      viewport: window.innerWidth,
      darkMode: !!(document.body && document.body.classList.contains('dark-mode')),
      own: {
        color: style.color || '',
        backgroundColor: style.backgroundColor || '',
        borderColor: style.borderColor || '',
        borderStyle: style.borderStyle || '',
        borderWidth: style.borderWidth || '',
        fontSize: style.fontSize || '',
        fontWeight: style.fontWeight || '',
        fontFamily: style.fontFamily || '',
        textAlign: style.textAlign || '',
        direction: style.direction || '',
        rootFontSize: rootStyle.fontSize || '16px',
        authoredFontSize: authoredFontSize,
        objectFit: style.objectFit || '',
        objectPosition: style.objectPosition || '',
        aspectRatio: style.aspectRatio || '',
        effectiveBackground: _pbEffectiveBackground(el)
      },
      spacing: getSpacingStyleBlock(style),
      spacingTemplate: getSpacingTemplateForEditable(el),
      pictureSpacing: (el.parentElement && el.parentElement.tagName === 'PICTURE')
        ? getSpacingStyleBlock(getComputedStyle(el.parentElement))
        : null,
      tokens: {
        color: _pbMatchToken(colorMap, tokenNames, style.color),
        backgroundColor: _pbMatchToken(colorMap, tokenNames, style.backgroundColor),
        borderColor: _pbMatchToken(colorMap, tokenNames, style.borderColor)
      },
      inherited: { color: parentEl ? (parentColor === style.color) : false },
      resolved: (data.resolveValues && typeof data.resolveValues === 'object')
        ? _pbResolveIntendedValues(el, data.resolveValues)
        : null,
      rootTokens: rootTokens
    };
  }
`;
}
